/**
 * Cache invalidation.
 *
 * Two granularity levels:
 *   - `invalidate(userId, "recipe")` — drop every cached read for that user
 *     within the `recipe.*` procedure family (list, get, slugHistory, etc.).
 *     Called after a recipe-mutating RPC succeeds.
 *   - `invalidateAll(userId)` — drop every cached read for that user. Called
 *     on sign-out so the next sign-in cannot serve the previous user's data
 *     under a fresh session.
 *
 * Why prefix-based and not entry-by-entry:
 *   At MVP we don't track which `recipe.list` queries reference recipe X.
 *   The conservative invariant is "if any recipe changed, every recipe-list
 *   slice on this user is suspect" — drop all of them and let the next read
 *   refetch. This is the same pattern TanStack Query uses with
 *   `queryClient.invalidateQueries({ queryKey: ['recipe'] })`.
 *
 *   With four read procedures, ~100 entries max in the cache, and a
 *   round-trip back to the server on the next read, the over-invalidation
 *   cost is negligible. If hot-path profiling later shows otherwise, swap in
 *   per-entry invalidation with a recipe→queries reverse index.
 *
 * Ref: tasks/pr-body-p4-followup-3.md Path A.
 */

import { delByPrefix } from "./store.js";
import { procedurePrefix, userPrefix } from "./key.js";

/**
 * Drop every cached entry for a procedure family on the given user.
 *
 * Example:
 *   await invalidate("u1", "recipe");
 *   // drops recipe.list (any input), recipe.get (any input), recipe.slugHistory.
 */
export async function invalidate(userId: string | null, procedureFamily: string): Promise<void> {
  const prefix = procedurePrefix({ userId, procedure: procedureFamily });
  await delByPrefix(prefix);
}

/**
 * Drop every cached entry for the given user across every procedure family.
 * Call this on sign-out BEFORE the auth state is cleared, so the userId is
 * still available for keying. Call sites: see `apps/web/src/routes/app.tsx`
 * `handleSignOut`.
 */
export async function invalidateAll(userId: string | null): Promise<void> {
  const prefix = userPrefix(userId);
  await delByPrefix(prefix);
}
